// src/pages/Home/UpcomingEvents.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaCalendarAlt, FaMapMarkerAlt, FaInfoCircle } from "react-icons/fa";
import { getUpcomingEvents } from "./../../services/eventServices";
import Button from "./../../components/Button/Button";
import { useUser } from "./../../contexts/UserContext";

const EVENTS_PER_PAGE = 3;

const UpcomingEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  const navigate = useNavigate();
  const { user } = useUser();

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const upcomingEvents = await getUpcomingEvents();
        // On garde tout ce que renvoie le back (jusqu'à 10)
        setEvents(upcomingEvents || []);
      } catch (error) {
        console.error("Failed to fetch events", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(events.length / EVENTS_PER_PAGE)),
    [events.length]
  );

  // Sélection des 3 events de la page courante
  const eventsToDisplay = useMemo(() => {
    if (!events.length) return [];
    const start = page * EVENTS_PER_PAGE;
    return events.slice(start, start + EVENTS_PER_PAGE);
  }, [events, page]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return { day: "", month: "" };
    const d = new Date(dateString);
    return {
      day: d.toLocaleDateString("fr-FR", { day: "2-digit" }),
      month: d
        .toLocaleDateString("fr-FR", { month: "short" })
        .replace(".", "")
        .toUpperCase(),
    };
  };

  const handleMoreInfo = () => {
    if (!user) {
      alert("Veuillez vous connecter pour accéder à cette page.");
      navigate("/login");
    } else {
      navigate("/calendrier");
    }
  };

  const goNextPage = () => {
    // navigation cyclique
    setPage((prev) => (prev + 1) % pageCount);
  };

  const goPrevPage = () => {
    setPage((prev) => (prev - 1 + pageCount) % pageCount);
  };

  /* ========== Loading ========== */
  if (loading) {
    return (
      <section className="py-10 md:py-14 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center text-center mb-6 md:mb-8 animate-pulse">
            <div className="h-6 w-64 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
            <div className="h-4 w-40 bg-slate-200 dark:bg-slate-700 rounded" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="rounded-2xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 h-64"
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  /* ========== Pas d'événements ========== */
  if (!events.length) {
    return (
      <section className="py-10 md:py-14 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-primary mb-1">
            Calendrier
          </p>
          <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight mb-2">
            Prochains événements & compétitions
          </h2>
          <p className="text-sm md:text-base text-slate-600 dark:text-slate-400">
            Aucun événement n’est programmé pour le moment. Le calendrier sera
            mis à jour prochainement.
          </p>
        </div>
      </section>
    );
  }

  /* ========== Affichage normal ========== */
  return (
    <section className="py-10 md:py-14 bg-slate-50 dark:bg-slate-900" id="events">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Titre */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 mb-6 md:mb-8">
          <div>
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-primary mb-1">
              Calendrier
            </p>
            <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight">
              Prochains événements & compétitions
            </h2>
          </div>
          <p className="text-xs md:text-sm text-slate-600 dark:text-slate-400 max-w-md">
            Un aperçu des rendez-vous à venir pour les judokas et clubs
            girondins.
          </p>
        </div>

        {/* Cartes événements : 3 par "page" */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {eventsToDisplay.map((event) => {
            const { day, month } = formatShortDate(event.start);
            const fullDate = formatDate(event.start);
            const fullEnd =
              event.end && event.end !== "0000-00-00"
                ? formatDate(event.end)
                : null;

            return (
              <article
                key={event.id}
                className="group flex flex-col bg-white dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-200"
              >
                {/* Image + badge date */}
                <div className="relative h-44 md:h-48 overflow-hidden">
                  <img
                    src={
                      event.photo_url
                        ? event.photo_url
                        : "/img/default_image.jpg"
                    }
                    alt={event.titre}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-transparent pointer-events-none" />
                  {/* Badge date */}
                  {day && (
                    <div className="absolute left-3 top-3 bg-white/95 rounded-xl px-2 py-1 text-center leading-tight shadow-md">
                      <div className="text-xs font-bold text-slate-900">
                        {day}
                      </div>
                      <div className="text-[10px] font-semibold text-primary tracking-[0.08em]">
                        {month}
                      </div>
                    </div>
                  )}
                  {/* Catégorie si existante */}
                  {event.cate_event && (
                    <div className="absolute right-3 top-3 bg-orange-500/90 text-xs uppercase font-semibold text-white px-2 py-1 rounded-full shadow-md">
                      {event.cate_event}
                    </div>
                  )}
                </div>

                {/* Contenu */}
                <div className="flex flex-col flex-grow p-4 space-y-3">
                  <h3 className="text-base md:text-lg font-semibold text-slate-900 dark:text-slate-50 line-clamp-2">
                    {event.titre}
                  </h3>

                  {event.description && (
                    <div
                      className="text-xs md:text-sm text-slate-700 dark:text-slate-300 line-clamp-4"
                      dangerouslySetInnerHTML={{
                        __html: event.description,
                      }}
                    />
                  )}

                  <div className="mt-1 space-y-1.5 text-xs md:text-sm text-slate-700 dark:text-slate-300">
                    <div className="flex items-center gap-2">
                      <FaCalendarAlt className="text-primary" />
                      <span>
                        {fullDate}
                        {fullEnd && <> – {fullEnd}</>}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaMapMarkerAlt className="text-primary" />
                      <span>{event.lieu_event || "Lieu à préciser"}</span>
                    </div>
                  </div>
                </div>

                {/* Bouton "Plus d'infos" (si utilisateur connecté) */}
                {user && (
                  <div className="px-4 pb-4">
                    <Button
                      text="Plus d'infos"
                      onClick={handleMoreInfo}
                      icon={FaInfoCircle}
                      className="mt-1 gap-2 w-full justify-center"
                    />
                  </div>
                )}
              </article>
            );
          })}
        </div>

        {/* Navigation pagination + CTA calendrier */}
        <div className="mt-6 md:mt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Pagination par pages de 3 */}
          {pageCount > 1 && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={goPrevPage}
                className="px-3 py-1.5 rounded-full border border-slate-300 dark:border-slate-600 text-xs text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                ◀ Précédents
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: pageCount }).map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setPage(idx)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === page
                        ? "bg-primary"
                        : "bg-slate-300 dark:bg-slate-600"
                    }`}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={goNextPage}
                className="px-3 py-1.5 rounded-full border border-slate-300 dark:border-slate-600 text-xs text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                Suivants ▶
              </button>
            </div>
          )}

          {/* CTA global vers le calendrier */}
          <button
            type="button"
            onClick={handleMoreInfo}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary text-primary text-xs md:text-sm font-semibold uppercase tracking-[0.18em] bg-white dark:bg-slate-900 hover:bg-primary hover:text-white transition-colors"
          >
            <FaCalendarAlt className="text-sm" />
            Voir tout le calendrier
          </button>
        </div>
      </div>
    </section>
  );
};

export default UpcomingEvents;
